import { useEffect, useState } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./MessageMetricsPage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface DailyPoint { date: string; label: string; sends: number; failures: number; }
interface FailureReason { reason: string; count: number; }
interface MetricsData {
  sends24h:    number;
  failures24h: number;
  failureRate: number;
  daily:       DailyPoint[];
  topFailures: FailureReason[];
}

const CHART_TOOLTIP = {
  contentStyle: {
    background: "#0E1E38", border: "1px solid rgba(201,168,76,0.2)",
    borderRadius: 10, fontSize: 12, color: "#F2EBD9",
  },
  labelStyle: { color: "rgba(242,235,217,0.5)" },
};

function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div className="mm-stat-card">
      <p className="mm-stat-label">{label}</p>
      <p className="mm-stat-value" style={accent ? { color: accent } : undefined}>{value}</p>
      {sub && <p className="mm-stat-sub">{sub}</p>}
    </div>
  );
}

export default function MessageMetricsPage() {
  const [data, setData]       = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  useEffect(() => {
    axios.get(`${API_BASE}/api/dashboard/message-metrics`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="mm-page">
        <div className="mm-header">
          <div>
            <h1 className="mm-title">Message Delivery</h1>
            <p className="mm-subtitle">WhatsApp send performance — last 7 days</p>
          </div>
        </div>

        {loading && <div className="mm-loading">Loading metrics…</div>}
        {error   && <div className="mm-error">Failed to load: {error}</div>}

        {data && (
          <>
            {/* Stat cards */}
            <div className="mm-stats-row">
              <StatCard
                label="Messages Sent (24h)"
                value={data.sends24h.toLocaleString()}
                sub="AI + staff replies"
                accent="#34d399"
              />
              <StatCard
                label="AI Replies (7d)"
                value={(data.topFailures.find(f => f.reason === "AI replies")?.count ?? 0).toLocaleString()}
                sub="bot-generated"
                accent="#C9A84C"
              />
              <StatCard
                label="Staff Replies (7d)"
                value={(data.topFailures.find(f => f.reason === "Staff replies")?.count ?? 0).toLocaleString()}
                sub="human-sent"
                accent="#60a5fa"
              />
            </div>

            {/* Daily bar chart */}
            <div className="mm-card">
              <p className="mm-card-title">Daily Outgoing Messages — Last 7 Days</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={data.daily} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(201,168,76,0.08)" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "rgba(242,235,217,0.45)", fontSize: 11 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: "rgba(242,235,217,0.45)", fontSize: 11 }}
                    axisLine={false} tickLine={false} width={32}
                    allowDecimals={false}
                  />
                  <Tooltip {...CHART_TOOLTIP} />
                  <Legend
                    wrapperStyle={{ fontSize: 12, color: "rgba(242,235,217,0.55)", paddingTop: 8 }}
                  />
                  <Bar dataKey="sends" name="Messages" fill="#34d399" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Sender breakdown */}
            <div className="mm-card">
              <p className="mm-card-title">Sender Breakdown — Last 7 Days</p>
              {data.topFailures.length === 0 ? (
                <p className="mm-empty">No messages in the last 7 days</p>
              ) : (
                <table className="mm-table">
                  <thead>
                    <tr>
                      <th>Sender</th>
                      <th>Messages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topFailures.map((f, i) => (
                      <tr key={i}>
                        <td className="mm-reason-cell">{f.reason}</td>
                        <td>
                          <span className="mm-count-badge">{f.count}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
